import {
    PLACEHOLDER_CONFIGS,
    PlaceholderConfig,
} from 'components/customer/RoomPlanner/helpers/placeholder/loadPlaceholderGLB';
import {PlaceholderFace} from 'components/customer/RoomPlanner/entity/PlaceholderFace';

const FALLBACK_BY_CATEGORY = new Map<string, string>([
    ['Fridge', '/templates/3D/models/fridge.glb'],
    ['Oven', '/templates/3D/models/oven.glb'],
]);

/** Display order for appliance subcategories (only shown when they have items). */
const CATEGORY_ORDER = [
    'Oven',
    'Rangehood',
    'Dishwasher',
    'Fridge',
    'Microwave',
    'Cooktop',
    'Void',
] as const;

export type PlaceholderCatalogItem = {
    face: PlaceholderFace;
    config: PlaceholderConfig;
};

/**
 * Appliance `subCategory` in the URL is a category name (Oven, Fridge, …).
 * Numeric values are leftover cabinet sub-style ids from Add Product, which
 * would otherwise empty the placeholder list (“No items in this category”).
 * @param {string | null | undefined} subCategory URL subCategory param
 * @return {string} Placeholder category name, or '' to show the full catalog
 */
export const getPlaceholderCatalogSubCategory = (
    subCategory: string | null | undefined
): string => {
    const value = subCategory?.trim() ?? '';
    if (!value || /^\d+$/.test(value)) {
        return '';
    }
    return value;
};

/**
 * Build a RoomPlanner placeholder config from an admin Placeholder Face.
 * Returns null when the face has no usable GLB template / fallback.
 * @param {PlaceholderFace} face Admin placeholder face
 * @return {PlaceholderConfig | null} Config used to load the GLB, or null
 */
export const toPlaceholderConfig = (
    face: PlaceholderFace
): PlaceholderConfig | null => {
    const category = face.category || '';
    const url =
        face.template ||
        FALLBACK_BY_CATEGORY.get(category) ||
        PLACEHOLDER_CONFIGS.find((c) => c.id.includes(category.toLowerCase()))
            ?.url;

    if (!url) return null;

    const style = Number(face.style);
    const height = Number(face.height);
    const width = Number(face.width);
    const depth = Number(face.depth);
    const hasCatalogSize =
        Number.isFinite(height) &&
        height > 0 &&
        Number.isFinite(width) &&
        width > 0 &&
        Number.isFinite(depth) &&
        depth > 0;

    return {
        url,
        id: `placeholder-${face.id}`,
        name: face.name,
        category: face.category,
        style: Number.isFinite(style) ? style : undefined,
        placeholderFaceId: face.id,
        ...(hasCatalogSize
            ? {
                  targetHeightMm: height,
                  targetWidthMm: width,
                  targetDepthMm: depth,
              }
            : {}),
    };
};

/**
 * Faces that can actually be placed in RoomPlanner.
 * @param {PlaceholderFace[]} faces Admin placeholder models
 * @return {PlaceholderCatalogItem[]} Placeable model/config pairs
 */
export const getPlaceablePlaceholderItems = (
    faces: PlaceholderFace[]
): PlaceholderCatalogItem[] =>
    faces
        .map((face) => ({
            face,
            config: toPlaceholderConfig(face),
        }))
        .filter((item): item is PlaceholderCatalogItem => item.config != null);

/**
 * Categories that currently have at least one placeable item.
 * Known categories first (fixed order), then any extras alphabetically.
 * @param {PlaceholderFace[]} faces Admin placeholder model
 * @return {string[]} Category names that have placeable items
 */
export const getPlaceholderCategoriesWithItems = (
    faces: PlaceholderFace[]
): string[] => {
    const items = getPlaceablePlaceholderItems(faces);
    const present = new Set<string>(
        items
            .map(({face}) => face.category)
            .filter((category) => category.length > 0)
    );

    const ordered = CATEGORY_ORDER.filter((category) => present.has(category));
    const extras = Array.from(present)
        .filter((category) => {
            const knownCategories: readonly string[] = CATEGORY_ORDER;
            return !knownCategories.includes(category);
        })
        .sort((a, b) => a.localeCompare(b));

    return ordered.concat(extras);
};

/**
 * Filter catalog items by search text (name, category, and dimensions).
 * All whitespace-separated tokens must match.
 *
 * @param {PlaceholderCatalogItem[]} items Catalog items to filter
 * @param {string} search Raw search box value
 * @return {PlaceholderCatalogItem[]} Items that match the search
 */
export const filterPlaceholderCatalogItems = (
    items: PlaceholderCatalogItem[],
    search: string
): PlaceholderCatalogItem[] => {
    const tokens = search
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter((token) => token.length > 0);

    if (tokens.length === 0) {
        return items;
    }

    return items.filter(({face}) => {
        const haystack = [
            face.name,
            face.category,
            String(face.width ?? ''),
            String(face.height ?? ''),
            String(face.depth ?? ''),
        ]
            .join(' ')
            .toLowerCase();

        return tokens.every((token) => haystack.includes(token));
    });
};
